/**
 * The ranges an effect preset may use (ADR-141 decision A2). Validation, the studio's fields and the client's read of the
 * manifest all compare against these, so they are the one knob for what a hand-edited or stale preset may ask of a client; the
 * light layer's knobs (decisions D2, D5 and D7) sit beside them.
 */

/**
 * The most particles one emitter may hold alive at once: `rate * life.max / 1000 + burst`, summed over its sub-kinds.
 */
export const MAX_PARTICLES_PER_EMITTER = 64;

/**
 * The most sub-kinds in one particle preset.
 */
export const MAX_SUB_KINDS = 4;

/**
 * The range of a preset's emission rate, in particles a second.
 */
export const RATE_RANGE = Object.freeze({ min: 0.1, max: 60 });

/**
 * The most particles a preset emits in one burst at start.
 */
export const BURST_MAX = 32;

/**
 * The range of a particle's life, in milliseconds.
 */
export const LIFE_RANGE_MS = Object.freeze({ min: 100, max: 8000 });

/**
 * The range of a particle's side, in pixels, and the most size entries a sub-kind lists.
 */
export const SIZE_RANGE_PX = Object.freeze({ min: 1, max: 8 });
export const MAX_SIZE_ENTRIES = 4;

/**
 * The range of a particle's speed, in pixels a second.
 */
export const SPEED_RANGE = Object.freeze({ min: 0, max: 600 });

/**
 * The range of gravity, in pixels a second squared.
 */
export const GRAVITY_RANGE = Object.freeze({ min: -200, max: 200 });

/**
 * The range of a particle's streak: how many pixels longer than wide it is drawn along its motion (0 is a square).
 */
export const STREAK_RANGE = Object.freeze({ min: 0, max: 12 });

/**
 * The range of the sideways sway: amplitude in pixels and frequency in hertz.
 */
export const SWAY_AMPLITUDE_RANGE = Object.freeze({ min: 0, max: 8 });
export const SWAY_HZ_RANGE = Object.freeze({ min: 0, max: 4 });

/**
 * How many stops a colour ramp and an alpha ramp may have.
 */
export const COLOUR_STOPS = Object.freeze({ min: 2, max: 6 });
export const ALPHA_STOPS = Object.freeze({ min: 2, max: 4 });

/**
 * The range of a light's radius in pixels, its intensity, its flicker amount and its flicker rate in hertz.
 */
export const LIGHT_RADIUS_RANGE = Object.freeze({ min: 8, max: 512 });
export const LIGHT_INTENSITY_RANGE = Object.freeze({ min: 0, max: 2 });
export const FLICKER_HZ_RANGE = Object.freeze({ min: 0, max: 12 });

/**
 * The most rectangles an area attachment may list.
 */
export const MAX_AREA_RECTS = 8;

/**
 * The longest id a preset may have.
 */
export const MAX_PRESET_ID = 40;

/**
 * The darkness texture's size as a share of the camera's resolution on each axis.
 */
export const LIGHT_SCALE = 0.5;

/**
 * The share of its night strength a light keeps at full day: a faint glow, never nothing.
 */
export const DAY_GLOW = 0.18;

/**
 * The most emitters and particles an effects view holds at once (ADR-141 decision F15). Beyond them the farthest
 * emitters stop until others leave.
 */
export const MAX_EMITTERS_PER_VIEW = 96;
export const MAX_PARTICLES_PER_VIEW = 1500;

/**
 * The most lights one view draws; the nearest to the camera's centre win.
 */
export const MAX_LIGHTS_PER_VIEW = 48;

/**
 * The most an indoor map may be dimmed (`atmosphere.dim`, ADR-141 decision D4): a room's light is held at one minus its
 * dim at every hour, so even the darkest cellar keeps 40 percent of daylight.
 */
export const MAX_MAP_DIM = 0.6;
